import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../components/ui/button";
import Common from "../../helper/common";
import AdvanceTable from "../../components/advanceTable";
import {
  AppSearchSelect,
  AppInput,
} from "../../components/appComponents";
import { useNavigate } from "react-router-dom";
import { useQueryParams } from "../../contexts/queryParamsContext";
import {
  respEnum,
  OpenMatchTypeEnum,
  OpenMatchStatusEnum,
  DateminFormat,
} from "../../constants/appConstant";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import moment from "moment";

const ManagerScreen = () => {
  const navigate = useNavigate();
  const { params, setParams } = useQueryParams();
  const [data, setData] = useState({});
  const [clubId, setClubId] = useState(null);
  const [sportId, setSportId] = useState(null);
  const [catId, setCatId] = useState(null);
  const [status, setStatus] = useState(null);
  const [type, setType] = useState(null);
  const [isResultUploaded, setIsResultUploaded] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [tillDate, setTillDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [clubs, setClubs] = useState([]);
  const [sports, setSports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getData(1);
    fetchInitialData();
  }, []);

  useEffect(() => {
    getData(1);
  }, [
    clubId,
    sportId,
    catId,
    status,
    type,
    isResultUploaded,
    fromDate,
    tillDate,
  ]);

  const resetFilters = () => {
    setClubId(null);
    setSportId(null);
    setCatId(null);
    setStatus(null);
    setType(null);
    setIsResultUploaded(null);
    setFromDate(null);
    setTillDate(null);
    setSearchTerm("");
  };

  const fetchInitialData = async () => {
    try {
      const [clubsRes, sportsRes, categoriesRes] = await Promise.all([
        Common.ApiService.getInstance().request("GetOpenMatchClubsDDL", null, "GET", false),
        Common.ApiService.getInstance().request("GetSports?isactive=true", null, "GET", false),
        Common.ApiService.getInstance().request("GetOpenMatchCategoriesDDL", null, "GET", false),
      ]);

      if (clubsRes?.status === respEnum.Success && clubsRes.data) {
        const clubsData = Array.isArray(clubsRes.data) ? clubsRes.data : [];
        setClubs([
          { id: null, name: "All Clubs" },
          ...clubsData.map((c) => ({ id: c.id, name: c.name })),
        ]);
      }
      if (sportsRes?.status === respEnum.Success && sportsRes.data) {
        const sportsData = Array.isArray(sportsRes.data) ? sportsRes.data : [];
        setSports([
          { id: null, name: "All Sports" },
          ...sportsData.map((s) => ({ id: s.id, name: s.name })),
        ]);
      }
      if (categoriesRes?.status === respEnum.Success && categoriesRes.data) {
        const categoriesData = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
        setCategories([
          { id: null, name: "All Categories" },
          ...categoriesData.map((c) => ({ id: c.id, name: c.name })),
        ]);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
    }
  };

  const getData = async (
    pageNumber,
    searchTerm = "",
    recordsToDisplay = null
  ) => {
    try {
      setIsLoading(true);
      const payload = {
        pageNumber: pageNumber,
        q: searchTerm || "",
        recordsToDisplay,
        clubId: clubId?.id || clubId || null,
        sportId: sportId?.id || sportId || null,
        cat_id: catId?.id || catId || null,
        status: status,
        type: type,
        isResultUploaded: isResultUploaded,
        fromDate: fromDate,
        tillDate: tillDate,
      };

      // Remove null/undefined values
      Object.keys(payload).forEach(
        (key) =>
          payload[key] === null ||
            payload[key] === undefined ||
            payload[key] === ""
            ? delete payload[key]
            : {}
      );

      const response = await Common.ApiService.getInstance().request(
        "GetOpenMatchesAdmin",
        payload,
        "POST",
        false
      );

      if (response?.status === respEnum.Success && response?.data) {
        const responseData = response.data;
        const matchesArray = responseData.data || [];

        let obj = {
          ...responseData,
          data: matchesArray.map((item) => ({
            ...item,
            start_datetime: item.start_datetime
              ? Common.Utility.ConvertTime(item.start_datetime)
              : null,
            end_datetime: item.end_datetime
              ? Common.Utility.ConvertTime(item.end_datetime)
              : null,
            status: Common.Utility.getKeyByValue(OpenMatchStatusEnum, item.status) || item.status,
            type: Common.Utility.getKeyByValue(OpenMatchTypeEnum, item.type) || item.type,
            is_result_uploaded: item.is_result_uploaded ? "Yes" : "No",
          })),
        };
        setData(obj);
      } else {
        setData({ data: [], totalRecords: 0 });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching matches data");
      setData({ data: [], totalRecords: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevertResult = async (matchId) => {
    const result = await Swal.fire({
      title: "Revert Result?",
      text: "Are you sure you want to revert the result? This will undo all point changes.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Revert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await Common.ApiService.getInstance().request(
        `RevertOpenMatchResult?openMatchId=${matchId}`,
        null,
        "POST",
        false
      );

      if (response?.status === respEnum.Success) {
        toast.success("Result reverted successfully!");
        getData(1);
      } else {
        toast.error(response?.message || "Failed to revert result");
      }
    } catch (error) {
      console.error("Error reverting result:", error);
      toast.error("An error occurred");
    }
  };

  const getStatusLabel = (statusValue) => {
    // Handle both string (converted enum) and number values
    const status = typeof statusValue === 'string' ? statusValue : Number(statusValue);
    const statusMap = {
      [OpenMatchStatusEnum.Pending]: {
        label: "Pending",
        class: "bg-yellow-100 text-yellow-800"
      },
      [OpenMatchStatusEnum.Completed]: {
        label: "Completed",
        class: "bg-green-100 text-green-800"
      },
      [OpenMatchStatusEnum.Cancelled]: {
        label: "Cancelled",
        class: "bg-red-100 text-red-800"
      },
      // Handle string values from converted enum
      "Pending": {
        label: "Pending",
        class: "bg-yellow-100 text-yellow-800"
      },
      "Completed": {
        label: "Completed",
        class: "bg-green-100 text-green-800"
      },
      "Cancelled": {
        label: "Cancelled",
        class: "bg-red-100 text-red-800"
      },
    };
    const statusInfo = statusMap[status] || {
      label: statusValue || "Unknown",
      class: "bg-gray-100 text-gray-800"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getTypeLabel = (typeValue) => {
    // Handle both string (converted enum) and number values
    const type = typeof typeValue === 'string' ? typeValue : Number(typeValue);
    const typeMap = {
      [OpenMatchTypeEnum.Open]: {
        label: "Open",
        class: "bg-blue-100 text-blue-800"
      },
      [OpenMatchTypeEnum.Exclusive]: {
        label: "Exclusive",
        class: "bg-purple-100 text-purple-800"
      },
      // Handle string values from converted enum
      "Open": {
        label: "Open",
        class: "bg-blue-100 text-blue-800"
      },
      "Exclusive": {
        label: "Exclusive",
        class: "bg-purple-100 text-purple-800"
      },
    };
    const typeInfo = typeMap[type] || {
      label: typeValue || "Unknown",
      class: "bg-gray-100 text-gray-800"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.class}`}>
        {typeInfo.label}
      </span>
    );
  };

  const actions = [
    {
      type: "view",
      onClick: (val) => {
        setParams({
          ...params,
          openMatchId: val.id,
        });
        navigate("/app/openmatches/details");
      },
      icon: "heroicons:eye",
      color: "text-blue-600",
      tooltip: "View Details",
    },
  ];

  const _thead = [
    { title: "ID", type: "number", isVisible: false, key: "id" },
    {
      title: "Club",
      type: "custom",
      isVisible: true,
      key: "clubName",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.clubLogo && (
            <img
              src={row.clubLogo}
              alt={row.clubName}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <span className="font-medium text-[#0C1B3A]">{row.clubName}</span>
        </div>
      ),
    },
    {
      title: "Sport",
      type: "text",
      isVisible: true,
      key: "sportName",
      render: (row) => (
        <span className="text-gray-700">{row.sportName || "Not specified"}</span>
      ),
    },
    {
      title: "Start Date & Time",
      type: "custom",
      isVisible: true,
      key: "start_datetime",
      render: (row) => (
        <div>
          <div className="font-medium text-[#0C1B3A]">
            {row.start_datetime
              ? moment(row.start_datetime).format("DD MMM YYYY")
              : "Not specified"}
          </div>
          <div className="text-sm text-gray-500">
            {row.start_datetime
              ? moment(row.start_datetime).format("HH:mm")
              : ""}
          </div>
        </div>
      ),
    },
    {
      title: "End Date & Time",
      type: "custom",
      isVisible: true,
      key: "end_datetime",
      render: (row) => (
        <div>
          <div className="font-medium text-[#0C1B3A]">
            {row.end_datetime
              ? moment(row.end_datetime).format("DD MMM YYYY")
              : "Not specified"}
          </div>
          <div className="text-sm text-gray-500">
            {row.end_datetime
              ? moment(row.end_datetime).format("HH:mm")
              : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Player Count",
      type: "custom",
      isVisible: true,
      key: "playerCount",
      render: (row) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Icon icon="heroicons:users" className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-[#0C1B3A]">
              {row.currentPlayerCount || 0}/{row.playerCount || 0}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Side A: {row.playersOnSideA || 0} | Side B: {row.playersOnSideB || 0}
          </div>
        </div>
      ),
    },
    {
      title: "Type",
      type: "custom",
      isVisible: true,
      key: "type",
      render: (row) => getTypeLabel(row.type),
    },
    {
      title: "Creator",
      type: "custom",
      isVisible: true,
      key: "creatorName",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.creatorImage && (
            <img
              src={row.creatorImage}
              alt={row.creatorName}
              className="w-6 h-6 rounded-full object-cover"
            />
          )}
          <span className="text-gray-700">{row.creatorName || "Not specified"}</span>
        </div>
      ),
    },
    {
      title: "Status",
      type: "custom",
      isVisible: true,
      key: "status",
      render: (row) => getStatusLabel(row.status),
    },
    {
      title: "Result Uploaded",
      type: "custom",
      isVisible: true,
      key: "is_result_uploaded"
    },
  ];

  const _statusOptions = [
    { id: null, name: "All Status" },
    { id: OpenMatchStatusEnum.Pending, name: "Pending" },
    { id: OpenMatchStatusEnum.Completed, name: "Completed" },
    { id: OpenMatchStatusEnum.Cancelled, name: "Cancelled" },
  ];

  const _typeOptions = [
    { id: null, name: "All Types" },
    { id: OpenMatchTypeEnum.Open, name: "Open" },
    { id: OpenMatchTypeEnum.Exclusive, name: "Exclusive" },
  ];

  const _resultUploadedOptions = [
    { id: null, name: "All" },
    { id: true, name: "Uploaded" },
    { id: false, name: "Not Uploaded" },
  ];

  const renderFilters = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
      <AppSearchSelect
        value={clubId}
        onChange={setClubId}
        placeholder="Filter by Club"
        data={clubs}
        className="mb-0"
      />
      <AppSearchSelect
        value={sportId}
        onChange={setSportId}
        placeholder="Filter by Sport"
        data={sports}
        className="mb-0"
      />
      <AppSearchSelect
        value={catId}
        onChange={setCatId}
        placeholder="Filter by Category"
        data={categories}
        className="mb-0"
      />
      <AppSearchSelect
        value={status}
        onChange={setStatus}
        placeholder="Filter by Status"
        data={_statusOptions}
        className="mb-0"
      />
      <AppSearchSelect
        value={type}
        onChange={setType}
        placeholder="Filter by Type"
        data={_typeOptions}
        className="mb-0"
      />
      <AppSearchSelect
        value={isResultUploaded}
        onChange={setIsResultUploaded}
        placeholder="Filter by Result"
        data={_resultUploadedOptions}
        className="mb-0"
      />
      <AppInput
        type="date"
        value={fromDate || ""}
        onChange={(e) => setFromDate(e.target.value || null)}
        placeholder="From Date"
        className="mb-0"
      />
      <AppInput
        type="date"
        value={tillDate || ""}
        onChange={(e) => setTillDate(e.target.value || null)}
        placeholder="Till Date"
        className="mb-0"
      />
      <div className="flex gap-2">
        <Button
          onClick={resetFilters}
          variant="outline"
          className="border-[#0C1B3A] text-[#0C1B3A] hover:bg-[#e8eaf0] flex-1"
        >
          <Icon icon="heroicons:x-mark" className="mr-1" />
          Reset
        </Button>
      </div>
    </div>
  );

  const addButton = () => (
    <Button
      onClick={() => navigate("/app/openmatches/create")}
      className="bg-[#0C1B3A] hover:bg-[#1a2a4e] text-white border-0"
    >
      <Icon icon="heroicons:plus" className="mr-1" />
      Create Match
    </Button>
  );

  return (
    <div className="container mx-auto p-3">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold text-[#0C1B3A] flex items-center">
          <Icon icon="heroicons:calendar-days" className="mr-2" />
          Open Matches Manager
        </h1>
        {addButton()}
      </div>

      {renderFilters()}

      <AdvanceTable
        title="Open Matches"
        columns={_thead.filter((item) => item.isVisible)}
        data={data}
        getData={getData}
        showPagination={true}
        showAddButton={false}
        actionButtons={actions}
        isLoading={isLoading}
        showSearch={true}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </div>
  );
};

export default ManagerScreen;
